import { Injectable } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { AlertController, Platform } from "@ionic/angular";
import {
  InAppBrowser,
  InAppBrowserEvent,
  InAppBrowserObject,
} from "@ionic-native/in-app-browser/ngx";
import { WebservicesService } from "./../services/webservices/webservices.service";
import { UserSessionService } from "./../services/user-session/user-session.service";
import { CustomerModel } from "./../models/customer-model";
// ODR vendor adapters:
import { VendorAdapterInterface } from "./providers/vendor-adapters/vendor-adapter-interface";
import { NetentAdapter } from "./providers/vendor-adapters/netent";
import { IGTAdapter } from "./providers/vendor-adapters/igt";
import { PlayNGoAdapter } from "./providers/vendor-adapters/playngo";
import { NYXAdapter } from "./providers/vendor-adapters/nyx";
import { InternalAdapter } from "./providers/vendor-adapters/internal";
import { KambiAdapter } from "./providers/vendor-adapters/kambi";
import { EveriAdapter } from "./providers/vendor-adapters/everi";
import { AGSAdapter } from "./providers/vendor-adapters/ags";
import { SlingoAdapter } from "./providers/vendor-adapters/slingo";
import { SpingamesAdapter } from "./providers/vendor-adapters/spingames";
import { ConfigService } from "./../services/config/config.service";
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";
import { EvolutionRedtigerOSSAdapter } from "./providers/vendor-adapters/evolution/evolution-redtigeross";
import { EvolutionNetentAdapter } from "./providers/vendor-adapters/evolution/evolution-netent";
import { EvolutionLiveDealerAdapter } from "./providers/vendor-adapters/evolution/evolution-livedealer";
import { EvolutionFirstPersonAdapter } from "./providers/vendor-adapters/evolution/evolution-firstperson";
import { DGCAdapter } from "./providers/vendor-adapters/dgc";
import { MicrogamingAdapter } from "./providers/vendor-adapters/microgaming";
import { PragmaticAdapter } from "./providers/vendor-adapters/pragmatic";
import { GenericAdapter } from "./providers/vendor-adapters/generic";
import { EventsService } from "../services/event/event.service";
import { LobbyService } from "../services/lobby/lobby.service";
import { PlatformResponse } from "../interfaces/webservices-interface";
import { TimeService } from "../services/time-service/time.service";
import { MobileDataService } from "../services/mobile-data/mobile-data.service";
import { GamesService } from "../services/lobby/games.service";
import { formatDate } from "@angular/common";
//

declare var cordova: any;
/**
 * interfaces used by On-Demand Resources on iOS app
 */
export interface ODRSwiftInterface {
  startWebserver(
    port: number,
    vendor: string,
    docMap: any,
    success: (msg: any) => void,
    error: (err: any) => void
  ): void;
  stopWebserver(success: (msg: any) => void, error: (err: any) => void): void;
  requestGameByTag(
    key: string,
    options: any,
    success: (msg: any) => void,
    error: (err: any) => void
  ): void;
  //     launchOGS( options: any, success: (msg: any)=>void, error: (err: any)=>void ): void;
  //     OGSEvaluateJavascript( js: string, success: (msg: any)=>void, error: (err:any) => void): void;
  isGameClosed(
    options: any,
    success: (isclosed: boolean) => void,
    error: (err: any) => void
  ): void;
}

declare var ODR: ODRSwiftInterface;
export interface PalaOGSLauncherInterface {
  launchOGS(
    options: any,
    success: (msg: any) => void,
    error: (err: any) => void
  ): void;
  OGSEvaluateJavascript(
    js: string,
    success: (msg: any) => void,
    error: (err: any) => void
  ): void;
  isGameClosed(
    options: any,
    success: (isclosed: boolean) => void,
    error: (err: any) => void
  ): void;
  close(
    options: any,
    success: (isclosed: boolean) => void,
    error: (err: any) => void
  ): void;
}

declare var palaOGSLauncher: PalaOGSLauncherInterface;

export interface GameViewInterface {
  closed: boolean;
  on?(eventName: string): Observable<any>;
  close?(): void;
}

@Injectable()
export class ODRController {
  // local state:
  private odr_doc_map: any;
  private customer: CustomerModel;
  private errorPopup: any;
  //
  private vendorAdapterMap: object;
  public onlineTime: any;
  public onlineTimeInterval: any;
  public intervalId;
  public timeOnlineEnabled: boolean;
  private statsDialogDisplayTime: boolean;
  public netPositionDisplay: boolean = false;
  //Net Position
  public netWinAmount: string = "0.00";
  public netPosition = ["0.00"];
  public dataToDisplay: string;
  public netWinOperator: string = "+";
  public netPositionDelay: any;
  public vendorName: any;
  private isCloseButtonClicked: boolean = false;
  protected telemetrySubject: Subject<any>;
  constructor(
    private platform: Platform,
    private webservices: WebservicesService,
    private userSession: UserSessionService,
    private alertCtrl: AlertController,
    private events: EventsService,
    public config: ConfigService,
    private gameService: GamesService,
    public LobbyService: LobbyService,
    public timeService: TimeService,
    private screenOrientation: ScreenOrientation,
    private mobileData: MobileDataService,
    private iab: InAppBrowser
  ) {
    this.telemetrySubject = new Subject<string>();
    const nyxAdapter = new NYXAdapter(
      this.webservices,
      this.userSession,
      this.config,
      this.telemetrySubject
    );
    this.vendorAdapterMap = {
      ags: new AGSAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      dgc: new DGCAdapter(this.webservices, this.userSession, this.config),
      everi: new EveriAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      evolution_evolution: new EvolutionFirstPersonAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      evolution_livedealer: new EvolutionLiveDealerAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      evolution_netent: new EvolutionNetentAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      evolution_redtiger: new EvolutionRedtigerOSSAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      evolution_redtigeross: new EvolutionRedtigerOSSAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      igt: new IGTAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.platform,
        this.telemetrySubject
      ),
      playngo: new PlayNGoAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.platform,
        this.telemetrySubject
      ),
      internal: new InternalAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      kambi: new KambiAdapter(this.userSession, this.config),
      microgaming: new MicrogamingAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      netent: new NetentAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      nyx: nyxAdapter,
      pragmatic: new PragmaticAdapter(
        this.webservices,
        this.userSession,
        this.config
      ),
      sgi: nyxAdapter,
      slingo: new SlingoAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      aristocrat: new GenericAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
      spingames: new SpingamesAdapter(
        this.webservices,
        this.userSession,
        this.config,
        this.telemetrySubject
      ),
    };

    this.netPositionDelay =
      this.config.getConfig().FEATURE_NET_POSITION_DELAY || "0";
    this.timeOnlineEnabled =
      this.config.getConfig().FEATURE_SHOW_TIME_ONLINE == 1 ? true : false;

    this.statsDialogDisplayTime =
      this.config.getConfig().FEATURE_STATS_DIALOG_DISPLAY_TIME || 25000;

    this.netPositionDisplay = this.config.getConfig().FEATURE_SHOW_NET_POSITION ?? 0;

    this.userSession.getCustomer().subscribe((customer) => {
      this.customer = customer;
    });

  //   this.events.subscribe('event:authentication:logout',()=>{
  //     this.stopWebserver();
  //  });
   
  //   this.events.subscribe("event:livedealerclosed", () => {
  //     this.stopWebserver();
  //   });
  }

  public static clientAppJurisdiction(clientapp): string {
    const clientappJurs = {
      "PhoneiOSCasinoNJ-v1.0": "us_nj",
      "PhoneiOSCasinoPA-v1.0": "us_pa",
      "TabletiOSCasinoNJ-v1.0": "us_nj",
      "TabletiOSCasinoPA-v1.0": "us_pa",
      "PhoneiOSCasino-v1.0": "ca_on",
      "TabletiOSCasino-v1.0": "ca_on"
    };
    return clientappJurs[clientapp];
  }

  protected vendorAdapter(
    vendor: string,
    subVendorName?: string
  ): VendorAdapterInterface {
    // check if subVendorName contains spaces (a common human input error)
    if (subVendorName) {
      if (subVendorName.indexOf(" ") >= 0) {
        subVendorName = subVendorName.split(" ")[0];
      }
      subVendorName = subVendorName.toLowerCase();
    }

    if ("evolution" === vendor && subVendorName) {
      vendor = `${vendor}_${subVendorName}`;
      console.log("ODR: subVendorName added, vendor == ", vendor);
    }
    return this.vendorAdapterMap[vendor]
      ? this.vendorAdapterMap[vendor]
      : this.vendorAdapterMap["internal"];
  }

  protected async processLaunchError(err: string) {
    var errorString = "";
    if ("SUCCESS" == err) {
      return;
    }
    switch (err) {
      case "unzipFail":
        errorString = "ODR Resource Unzip Failure";
        break;
      case "TAG_NOT_FOUND_ERROR":
        errorString = "Resource Not Found";
        break;
      case "MULTIPLE_OBJECTS_IN_BUNDLE":
      case "NO_ZIPS_IN_BUNDLE":
        errorString = "Resource Bundle Invalid Format";
        break;
      case "NOT_ENOUGH_SPACE_ERROR":
        errorString = "Not Enough Free Space on Device";
        break;
      case "BUNDLE_EXCEEDS_MAX_SIZE_ERROR":
        errorString = "Resource Bundle exceeds maximum size";
        break;
      default:
        errorString = "Unknown error: " + err;
        break;
    }
    var gameStr = "";
    this.errorPopup = await this.alertCtrl.create({
      header: errorString,
      message: "",
      buttons: ["OK"],
    });
    setTimeout(() => {
      this.errorPopup.present();
    }, 500); // the delay here is to allow the cordova-plugin-odr-swift to stop updating the UI with loading status
  }

  /**
   * go through docMap, and for all keys that contain the substring '#BUNDLENAME', replace that text
   * with the final path element of the value.
   * also, convert the array of [loc,tag] into an object mapping locs to tags
   */
  protected processPathsubs(docMap: Array<any>): [Object, string] {
    var bundlename;
    var parsedDocMap = [];
    var index = 0;
    for (let tagobj of docMap) {
      var loc = tagobj.loc;
      var tag = tagobj.tag;
      bundlename = tag.split("/").pop();
      if (loc.indexOf("#BUNDLENAME") >= 0) {
        var newkey = loc.replace("#BUNDLENAME", bundlename);
        delete docMap[loc];
        //        parsedDocMap[newkey] = tag;
        var o = {};
        o[newkey] = tag;
        parsedDocMap.push(o);
      } else {
        //        parsedDocMap[loc] = tag;
        var o = {};
        o[loc] = tag;
        parsedDocMap.push(o);
      }
    }
    return [parsedDocMap, bundlename];
  }

  public maybeDownloadODR(gamePrms: any): Promise<any> {
    return new Promise((odrResolve) => {
      if (!this.platform.is("ios") || !this.platform.is("cordova")) {
        return odrResolve(gamePrms);
      }
      var params: any = {};
      if (gamePrms.gameLaunchId) {
        params.game_launcher_id = gamePrms.gameLaunchId;
      } else if (gamePrms.gameLauncherId) {
        params.game_launcher_id = gamePrms.gameLauncherId;
      }

      if (gamePrms.vendor) {
        params.vendor = gamePrms.vendor;
      }

      const processPayloadFn = async (response: PlatformResponse<any>) => {
        gamePrms["ODR"] = {};
        if (
          "undefined" === typeof response.payload.vendor_name &&
          "evolution" === response.payload.vendor
        ) {
          response.payload.vendor_name = "evolution";
        }
        Object.assign(gamePrms["ODR"], response.payload);
        //
        var vendorAdapter: VendorAdapterInterface = this.vendorAdapter(
          response.payload.vendor,
          response.payload.vendor_name
        );
        if (["launchOGS"].indexOf(vendorAdapter.windowStrategy()) >= 0) {
          return odrResolve(gamePrms);
        }
        var tagsLayout: Array<any> = await vendorAdapter.getTagLayout(
          response.payload
        );
        console.log("ODR: tagsLayout: ", tagsLayout);
        var shouldDescendIntoDirs = vendorAdapter.shouldDescendIntoDirs();
        var docMap = [];
        Promise.all(
          tagsLayout.map((tagobj: any) => {
            var { tag, loc } = tagobj;

            return new Promise<string>((resolve: any, reject: any) => {
              //DO NOT DELETE THIS VERY USEFUL BIT OF LOGS...
              console.log("ODR.requestGameByTag", tag);
              ODR.requestGameByTag(
                tag as string,
                { shouldDescendIntoDirs: shouldDescendIntoDirs },
                (path: string) => {
                  docMap.push({
                    loc: loc,
                    tag: path,
                  });
                  resolve();
                },
                (err: any) => {
                  reject(err);
                }
              );
            });
          })
        )
          .catch((err) => {
            console.log("ODR: openGameInTheWindow(): catch: ", err);
            this.processLaunchError(err);
            throw err;
          })
          .then(() => {
            var [parsedMap, bundlename] = this.processPathsubs(docMap);
            gamePrms.ODR.bundlename = bundlename;

            console.log(
              "launching webserver with map:",
              JSON.stringify(parsedMap)
            );
            var port = vendorAdapter.getServerPort
              ? vendorAdapter.getServerPort()
              : 9090;
            ODR.startWebserver(
              port,
              gamePrms.ODR.vendor,
              parsedMap,
              (msg: any) => {
                console.log("ODR: delaying after start of webserver");
                setTimeout(() => {
                  console.log("ODR: delay finished, now launching the game...");
                  this.odr_doc_map = parsedMap;
                  odrResolve(gamePrms);
                }, 0);
              },
              (err: any) => {
                window.alert(
                  "ODR: odr-controller.ts: startWebserver() error: " + err
                );
              }
            );

            this.gameService.getRecentGames();
          });
      };

      if (params.game_launcher_id) {
        this.webservices
          .get("games/get-casino-data", params)
          .subscribe(processPayloadFn);
      } else {
        processPayloadFn({
          result: true,
          payload: JSON.parse(JSON.stringify(gamePrms)),
        });
      }
    });
  }

  public setupTimeOnlineOverlay(vendorAdapter: VendorAdapterInterface) {
    let screenOrientationSubsctiption: Subscription;
    if ("function" === typeof vendorAdapter.evaluateJavascript) {
      var FEATURE_POPUPINTERVAL_MS =
        this.config.getConfig().FEATURE_POPUPINTERVAL_MS;
      this.injectSafeArea(vendorAdapter);
      if (!FEATURE_POPUPINTERVAL_MS) {
        FEATURE_POPUPINTERVAL_MS = 30 * 60 * 1000;
      }

      if (this.onlineTimeInterval) {
        clearInterval(this.onlineTimeInterval);
        this.onlineTimeInterval = null;
      }

      setTimeout(() => {
        this.injectTimeOnline(vendorAdapter);
      }, 8000);

      this.onlineTimeInterval = setInterval(() => {
        this.injectTimeOnline(vendorAdapter);
      }, 60 * 1000);

      screenOrientationSubsctiption = this.screenOrientation
        .onChange()
        .subscribe(() => {
          this.injectSafeArea(vendorAdapter);
          if (!!this.screenOrientationAdjustmentDebounceTimer) {
            clearTimeout(this.screenOrientationAdjustmentDebounceTimer);
          }

          this.screenOrientationAdjustmentDebounceTimer = setTimeout(() => {
            vendorAdapter.evaluateJavascript(`
                  var div;
                  div = document.getElementById("timeOnline");
                  if (div) {div.remove();}
                  div = document.getElementById( "sessionStats").remove();
                  if (div) {div.remove();}
                `);
            if (this.netPositionDisplay) {
              this.injectSessionStatsPopUp(
                vendorAdapter,
                this.netWinOperator,
                this.netWinAmount
              );
            }
            if (this.timeOnlineEnabled) {
              this.injectTimeOnline(vendorAdapter);
            }
            return;
          }, 500);

          return this.screenOrientationAdjustmentDebounceTimer;
        });
    } else {
      clearInterval(this.onlineTimeInterval);
      this.onlineTimeInterval = null;
    }
  }

  screenOrientationAdjustmentDebounceTimer = null;

  injectTimeOnline(vendorAdapter: VendorAdapterInterface): Promise<any> {
    let onlineTime = this.timeService.getOnlineTime() || "";

    console.log("time online: doing injection:", onlineTime);
    const isPortrait = this.platform.isPortrait();
    let vendorName = this.vendorName;
    console.log("vendor---", vendorName);

    const js = `
        if( ("undefined" !== typeof window) ) {
            console.log("time online: doing injection:");
            window.timeOnlinePopup = function() {

                function displayTimeOnline() {
                        var div;
                        var elem;
                        if( !document.getElementById( "timeOnline")) {
                            elem = document.getElementsByTagName( "body")[0];
                            div = document.createElement( "div");
                            div.id = "timeOnline";
                            div.style.position = "fixed";
                            div.style.padding = "5px 10px";
                            div.style.width = "auto";
                            div.style.background = "rgba(200,200,200,0.5)";
                            div.style.color = "#000";
                            div.style.zIndex = "999998";
                            div.style.fontFamily = "sans-serif";
                            elem.appendChild( div);
                        }
                        div = document.getElementById( "timeOnline");
                        if(div) {
                            div.style.top = '${
                              isPortrait
                                ? vendorName == "nyx"
                                  ? "32px"
                                  : "0px"
                                : vendorName == "nyx"
                                ? "25px"
                                : "13px"
                            }';
                            div.style.left = '${isPortrait ? "0px" : "13px"}';
                            div.style.fontSize = '${
                              isPortrait ? "1em" : "0.9em"
                            }';
                            div.innerHTML = "Time online: ${onlineTime}";
                        }

                    }
                    displayTimeOnline();
                }
                window.timeOnlinePopup();
            }
        `;
    return vendorAdapter.evaluateJavascript(js);
  }
  injectSafeArea(vendorAdapter: VendorAdapterInterface): Promise<any> {
    const insets = this.mobileData.insets.insets;
    const isLandscape = this.platform.isLandscape();

    let vendorName = this.vendorName;
    console.log("SafeArea---vendor---", vendorName);

    const landscapeJs = `
        if( ("undefined" !== typeof window) ) {
            console.log("Applying Safe Area:");
            window.applySafeArea = function() {

                function applySafeAreaLandscape() {
                    document.body.style.marginTop =  ${insets.top};
                    document.body.style.marginBottom =   ${insets.bottom};
                    document.body.style.marginRight =  ${insets.right};
                    document.body.style.marginLeft =  ${insets.left};
                            }
                    applySafeAreaLandscape();
                }
                window.applySafeArea();
            }
        `;
    const portraitJs = `
        if( ("undefined" !== typeof window) ) {
            console.log("Applying Safe Area:");
            window.applySafeArea = function() {

                function applySafeAreaLandscape() {
                    document.body.style.marginTop =  0px;
                    document.body.style.marginBottom =   0px;
                    document.body.style.marginRight =  0px;
                    document.body.style.marginLeft =  0px;
                            }
                    applySafeAreaLandscape();
                }
                window.applySafeArea();
            }
        `;
    let js = isLandscape ? landscapeJs : portraitJs;
    return vendorAdapter.evaluateJavascript(js);
  }

  injectSessionStatsPopUp(
    vendorAdapter: VendorAdapterInterface,
    operator,
    data
  ): Promise<any> {
    let netWinOperator = operator;
    let netWinAmount = data;
    let vendorName = this.vendorName;
    console.log("vendor---", vendorName);
    const isPortrait = this.platform.isPortrait();
    const js = `
        if( ("undefined" !== typeof window) ) {
            console.log( "session stats: doing injection:");

            window.sessionStatsPopup = function() {

                function displaySessionStats() {
                        var divWrapper;
                        var elemement;
                        if( !document.getElementById( "sessionStats")) {
                            elem = document.getElementsByTagName( "body")[0];
                            div = document.createElement( "div");
                            div.id = "sessionStats";
                            div.style.position = "fixed";
                            div.style.padding = "5px 10px";
                            div.style.width = "auto";
                            div.style.background = "rgba(200,200,200,0.5)";
                            div.style.color = "#000";
                            div.style.zIndex = "999998";
                            div.style.fontFamily = "sans-serif";
                            elem.appendChild( div);
                        }
                        div = document.getElementById( "sessionStats");
                        if(div) {
                            div.style.top = '${
                              isPortrait
                                ? vendorName == "nyx"
                                  ? "25px"
                                  : "0px"
                                : vendorName == "nyx"
                                ? "25px"
                                : "13px"
                            }';
                            div.style.right = '${isPortrait ? "0px" : "13px"}';
                            div.style.fontSize = '${
                              isPortrait ? "1em" : "0.9em"
                            }';
                            div.innerHTML = "Net Position: ${netWinOperator}$ ${netWinAmount}";
                        }

                    }
                    displaySessionStats();
                }
                window.sessionStatsPopup();
            }
        `;

    console.log("session stats: doing injection netWinAmount:", netWinAmount);

    if ("function" === typeof vendorAdapter.evaluateJavascript) {
      return vendorAdapter.evaluateJavascript(js);
    }
  }

  private window_strategies = {
    inappbrowser: this.launchInappbrowser,
    launchOGS: this.launchOGS,
  };

  protected launchInappbrowser(
    gamePrms: any,
    vendorAdapter: VendorAdapterInterface
  ) {
    var port = vendorAdapter.getServerPort
      ? vendorAdapter.getServerPort()
      : 9090;
    //        var windowRef = this.iab.create('http://localhost:9090/blank', '', 'location=no,footer=no,hardwareback=no,hidenavigationbuttons=yes,closebuttoncaption=< back,allowInlineMediaPlayback=yes');

    var windowRef = this.iab.create(
      `http://localhost:${port}/blank`,
      "_blank",
      "hidden=yes,location=no,footer=no,hardwareback=no,hidenavigationbuttons=yes,closebuttoncaption=< back,allowInlineMediaPlayback=yes"
    );
    vendorAdapter.setWindowRef(windowRef);
    let mythis = this;
    let subscriber: Subscription = windowRef
      .on("loadstop")
      .subscribe((evt: InAppBrowserEvent) => {
        windowRef.show(); // @todo move this statement to a better place to avoid displaying blank screen
        subscriber.unsubscribe();

        if (typeof gamePrms.session_token !== "undefined") {
          if (mythis.timeOnlineEnabled) {
            mythis.setupTimeOnlineOverlay(vendorAdapter);
          }

          this.injectGameMessage(vendorAdapter);
          this.gameMessageTracker(windowRef);
          this.injectMessageListener(vendorAdapter)
          if (mythis.netPositionDisplay) {
            setTimeout(() => {
              mythis.injectSessionStatsPopUp(vendorAdapter, "+", "0.00");
            }, 2500);
          }
        }

        vendorAdapter
          .credentials(gamePrms)
          .then((gamePrms) =>
            vendorAdapter.launchODR(windowRef as InAppBrowserObject, gamePrms)
          )
          .catch((err) => {
            clearInterval(mythis.onlineTimeInterval);
            mythis.onlineTimeInterval = null;

            let wr: InAppBrowserObject = windowRef as InAppBrowserObject;
            vendorAdapter.onGameClosed(windowRef, () => {
              console.log("ODR: INC-GAME: onGameClosed() callback...");
              const launchIncomplete = () => {
                Object.assign(gamePrms, err);
                mythis
                  .maybeDownloadODR(gamePrms)
                  .then((gamePrms: any) => mythis.launchODRByVendor(gamePrms));
              };
              window.alert(
                "You have an incomplete game, you will be taken to that game now."
              );
              ODR.stopWebserver(
                () => {
                  console.log("ODR: INC-GAME: webserver stopped.");
                  launchIncomplete();
                },
                (err) => {
                  console.log("ODR: INC-GAME: webserver stop failed: ", err);
                  launchIncomplete();
                }
              );
              mythis.events.publish("event:balanceState", { show: true });
            });
            wr.close();
          });
      });

    this.isCloseButtonClicked = false;
    let subscriberonMessage: Subscription = windowRef.on("message")
    .subscribe((event) => {
      console.log("messageCallBack", event);       
      //event from iab before closing the browser
      if(event && event.data === "beforeBrowserExit") {
        this.injectIabPostMessage(vendorAdapter, 'CLOSE_GAME');
        this.isCloseButtonClicked = true;
      }
      
      //acknowledge event from game after receiving CLOSE_GAME
      if(event && event.data && 
         event.data._type === "nw2Lobby.closegame.CloseGameResponse") {
         console.log("CloseGameResponse reveived and Closing game window");
         //Close browser window only if the close game event trigerred by the game
         //Do not close window again if the close button already clicked
        if(!this.isCloseButtonClicked && windowRef) windowRef.close();       
      }
    });

    let subscriberonExit: Subscription = (windowRef as InAppBrowserObject)
      .on("exit")
      .subscribe((evt: InAppBrowserEvent) => {
        subscriberonExit.unsubscribe();
        subscriberonMessage.unsubscribe();
        console.log("inapp exit");
        mythis.events.publish("event:empty:gamedata"); // to set empty live dealer game in lobbynavigator
        clearInterval(mythis.onlineTimeInterval);
        mythis.onlineTimeInterval = null;
        // mythis.events.unsubscribe('event:GameStats:Response');  //Needs to be added once unsubscribe method is available.
      });

    return windowRef;
  }

  protected launchOGS(
    gamePrms: any,
    vendorAdapter: VendorAdapterInterface
  ): GameViewInterface {
    var gameView = vendorAdapter.launchOGS(
      this.alertCtrl,
      vendorAdapter.credentials(gamePrms)
    );

    if (typeof gamePrms.session_token !== "undefined") {
      if (this.timeOnlineEnabled) {
        this.setupTimeOnlineOverlay(vendorAdapter);
      }
    }
    this.events.subscribe("event:sessonstats", (e) => {
      console.log("Session Stats Recieved at OGS " + e);
      this.injectSessionStatsPopUpPopover(vendorAdapter, e);
    });

    // vendorAdapter.onGameClosed( gameView, () => {
    //   this.events.unsubscribe("event:sessonstats");
    //   console.log('Session Stats unsubscribed at OGS');

    // });

    return gameView;
  }

  async injectSessionStatsPopUpPopover(
    vendorAdapter: VendorAdapterInterface,
    e
  ): Promise<any> {
    let time = e.parametersMap.time;
    let wager = e.parametersMap.wagerAmount;
    let netWinAmount = this.netWinAmount || "0.00";
    let statsDialogDisplayTime = this.statsDialogDisplayTime || 15000;
    console.log("session stats: doing injection time:", time);
    let currentTime = "";
    const PIPE_DATE_FORMAT = !!this.config.getConfig().FEATURE_TIME_FORMAT_TO
      ? this.config.getConfig().FEATURE_TIME_FORMAT_TO
      : "hh:mm a";
    this.timeService.getServerTime().then((serverTime: any) => {
      if (serverTime.result == 1) {
        currentTime = formatDate(
          serverTime.payload.full,
          PIPE_DATE_FORMAT,
          "en-US"
        );
      }

      const js = `
          if( ("undefined" !== typeof window) ) {
            console.log( "session stats script inside: doing injection:");

            window.sessionStatsPopupPopover = function() {
                console.log('Session Stats Script executed');
                function displaySessionStatsPopover() {
                        var divWrapper;
                        var elemement;
                        if( !document.getElementById( "sessionStatsPopover")) {
                            elem = document.getElementsByTagName( "body")[0];
                            div = document.createElement( "div");
                            div.id = "sessionStatsPopover";
                            div.style.position = "absolute";
                            div.style.top = "50%";
                            div.style.left = "50%";
                            div.style.width = "70%";
                            div.style.transform = "translate(-50%, -50%)";
                            div.style.background = "#fff";
                            div.style.borderRadius = "10px";
                            div.style.padding = "5px 10px";
                            div.style.color = "#000";
                            div.style.zIndex = "999999";
                            div.style.fontFamily = "sans-serif";
                            div.style.fontSize = "1rem";
                            elem.appendChild( div);
                        }
                        div = document.getElementById( "sessionStatsPopover");
                        
                        if(div) {
                          try{
                            div.addEventListener("click",function() {document.getElementById("sessionStatsPopover").remove();} )
                          } catch (e) {
                            console.log(e)
                          }
                            
                          div.style.marginLeft = "5px";
                          div.innerHTML = "<div style=' display:block;text-align:center;padding-bottom:15px; font-weight:bold;'>Session Stats<span style='color:black; display:block; width:10px; float:right'>x</span></div><h4>Current Session Stats</h4><p>Current Time: ${currentTime}</p> <p>Wager: $ ${wager}</p> <p>Time Online: ${time} minute(s)</p> <p>Net Win Amount: ${netWinAmount}</p> ";
                        }
                    }
                    function removePopUpPopover() {
                        setTimeout(function() { document.getElementById("sessionStatsPopover").remove(); }, ${statsDialogDisplayTime});
                    }
                    removePopUpPopover();
                    displaySessionStatsPopover();
                }
                window.sessionStatsPopupPopover();
          }`;
      return vendorAdapter.evaluateJavascript(js);
    });
  }

   
  public launchODRByVendor(gamePrms: any): Promise<VendorAdapterInterface> {
    console.log("ODR: launchODRByVendor(): ", JSON.stringify(gamePrms));

    return new Promise((resolve, reject) => {
      let vendor = gamePrms.ODR.vendor;
      this.vendorName = gamePrms.ODR.vendor;
      var windowRef: InAppBrowserObject | Window | GameViewInterface;
      let vendorAdapter = this.vendorAdapter(vendor, gamePrms.ODR.vendor_name);
      let strategy = vendorAdapter.windowStrategy();

      if (this.window_strategies[strategy]) {
        windowRef = this.window_strategies[strategy].apply(this, [
          gamePrms,
          vendorAdapter,
        ]);
      } else {
        var e =
          "FATAL ERROR: no implementation found for window strategy: `" +
          strategy +
          "`.";
        console.error(e);
        reject(e);
      }

      this.events.subscribe("event:GameStats:Response", (data) => {
        console.log("event:GameStats:Response----", data);
        console.log("-----received-------", data);

        this.dataToDisplay = data;
        //Delay the Net Position value to update on UI based on config value
        let timeout = 0;
        if (this.netPositionDelay) {
          if (this.netPositionDelay == 0) {
            timeout = 0;
          } else {
            timeout = this.netPositionDelay * 1000;
          }
        }
    //added configurable delay to net postion
        setTimeout(() => {
        //Adjusted call to display +/- and have to change type from number to string to display default value 0.00
        if (this.dataToDisplay.startsWith("-")) {
          this.netWinOperator = "-";
          this.netWinAmount = this.dataToDisplay.substring(1);
        } else {
          this.netWinOperator = "+";
          this.netWinAmount = this.dataToDisplay;
        }
        this.injectSessionStatsPopUp(vendorAdapter,this.netWinOperator, this.netWinAmount);
      },timeout);
      });

      this.events.subscribe("event:sessonstats", (e) => {
        console.log("Session Stats Recieved ", e);
        if (
          e &&
          (windowRef as InAppBrowserObject) &&
          "function" === typeof (windowRef as InAppBrowserObject).on
        ) {
          this.injectSessionStatsPopUpPopover(vendorAdapter, e);
        }
      });

      //IF game is closed then reset the netPosition with 0.00 to maintain the logic to display one hand behind for netWinAmount

      this.events.subscribe("event:reset:netWinAmount", () => {
        this.netPosition = ["0.00"];
        this.netWinOperator = "+";
        this.netWinAmount = "0.00";
      });

      vendorAdapter.onGameClosed(windowRef, () => {
        clearInterval(this.onlineTimeInterval);
        this.onlineTimeInterval = null;
        this.events.publish("event:iosgameclosed");
        this.gameService.getRecentGames();
        this.events.unsubscribe("event:sessonstats");
        ODR.stopWebserver(
          () => {
            console.log("ODR: webserver stopped.");
          },
          (err) => {
            console.log("ODR: webserver stop failed: ", err);
          }
        );
        this.events.publish("event:balanceState", { show: true });
        this.events.publish("event:reset:netWinAmount");
      });
      resolve(vendorAdapter);
    });
  }

  public stopWebserver(): void {
    ODR?.stopWebserver(
      () => {
        console.log("ODR: webserver stopped.");
      },
      (err) => {
        console.log("ODR: webserver stop failed: ", err);
      }
    );
  }

  public injectGameMessage(vendorAdapter: VendorAdapterInterface){
    const js = `
                function processGameMessagePlayngo(e){
                  switch(e.data.type){
                      case"backToLobby":
                          console.log("backToLobby");
                          window.iab_backToLobby=true;
                      break;
                      case"reloadGame":
                          console.log("reloadGame");
                          window.iab_reloadGame=true;
                      break;
                  }
                }
                window.addEventListener("message",this.processGameMessagePlayngo.bind(this));
                this.postMessage({messageType:"addEventListener",eventType:"backToLobby"},window.location.origin);
                this.postMessage({messageType:"addEventListener",eventType:"reloadGame"},window.location.origin);
             `;
    setTimeout(() => {
      vendorAdapter.evaluateJavascript(js);
    }, 2500);
  }

  public gameMessageTracker(windowRef: any){
    let backToLobbyInterval;
    let reloadGameInterval;
    setTimeout(() => {
      backToLobbyInterval = setInterval(() => {
        windowRef.executeScript({ code: "window.iab_backToLobby" }, val => {
            if (val[0]) {
              windowRef.close();
              clearInterval(backToLobbyInterval);
            }
        });
      }, 500)
      reloadGameInterval = setInterval(() => {
        windowRef.executeScript({ code: "window.iab_reloadGame" }, val => {
            if (val[0]) {
              console.log("receive window.iab_reloadGame message");
            }
        });
      }, 500)
    }, 2000);
  }

  //iab postmessage implemented for aristocrat
  private injectIabPostMessage(vendorAdapter: any, message: string) {
    const js = `  
                console.log('triggering event', '${message}');              
                window.postMessage({"message": "${message}"},"*");
                `;
    vendorAdapter.evaluateJavascript(js);                    
  }

  //Message listener
  private injectMessageListener(vendorAdapter: any) {
    const js = `                                 
                console.log('listening message');
                window.addEventListener("message", (event) => {
                  console.log("js receives messaage", event); 
                  webkit.messageHandlers.cordova_iab.postMessage(event.data);
                });      
                `;
    setTimeout(() => {
      vendorAdapter.evaluateJavascript(js);
    }, 2500);
  }
}